import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import type { ApiOutputs } from "@rosc/contracts";
import { PRE_CHART_STATUS, publicIdSchema } from "@rosc/contracts";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { AppConfig } from "@/common/appConfig";
import { REFRESH_AUDIO_INTERVAL } from "@/common/config";
import { MicDeviceType, StatusFile } from "@/common/enums/enums";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  parseVisitDetail,
  parseVisitPage,
  sortVisitSummaries,
  toVisitDetail,
  type VisitDetail,
  type VisitSummary,
} from "@/services/visits/read-model";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { publicTraversalError } from "./public-pagination";

export interface VisitScope {
  uid: string;
  accountPublicId: string;
  generation: number;
}
const scopeKey = (value: VisitScope): readonly unknown[] => [
  "visit-reads",
  value.uid,
  value.accountPublicId,
  value.generation,
];
let generation = 0;
let scope: VisitScope | null = null;
function updateScope(): void {
  const { user, userData } = useAuthStore.getState();
  const uid = user?.uid;
  const accountPublicId = userData?.public_id;
  if (uid === scope?.uid && accountPublicId === scope?.accountPublicId) return;
  const previous = scope;
  scope =
    uid && accountPublicId
      ? { uid, accountPublicId, generation: ++generation }
      : null;
  if (previous) {
    if (
      useRecordingSettingsStore.getState().micDevice?.type ===
      MicDeviceType.MOBILE
    )
      useRecordingSettingsStore.getState().saveMicDevice(null);
    for (const prefix of [
      "visit-reads",
      "account-usage",
      "template-reads",
      "template-library",
      "device-reads",
    ]) {
      const queryKey = [
        prefix,
        previous.uid,
        previous.accountPublicId,
        previous.generation,
      ];
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
    }
  }
}
useAuthStore.subscribe(updateScope);
export const getVisitScope = (): VisitScope | null => {
  updateScope();
  return scope;
};
export const useVisitScope = (): VisitScope | null =>
  useSyncExternalStore(
    (listener) => useAuthStore.subscribe(listener),
    getVisitScope,
  );
const disabledScope: VisitScope = {
  uid: "",
  accountPublicId: "",
  generation: 0,
};
const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    listVisits: {
      infiniteInterceptors: [
        async (options): Promise<ApiOutputs["listVisits"]> =>
          parseVisitPage(
            await options.next(),
            typeof options.input === "symbol"
              ? undefined
              : options.input.query?.cursor,
          ),
      ],
    },
    getVisit: {
      queryInterceptors: [
        async (options): Promise<ApiOutputs["getVisit"]> =>
          parseVisitDetail(
            await options.next(),
            typeof options.input === "symbol"
              ? ""
              : options.input.params.public_id,
          ),
      ],
    },
  },
});
export function visitListOptions(
  value: VisitScope,
): ReturnType<typeof orpc.listVisits.infiniteOptions<string | undefined>> {
  return orpc.listVisits.infiniteOptions({
    queryKey: [...scopeKey(value), "list"],
    input: (cursor: string | undefined) => ({
      params: {},
      query: {
        limit: "100",
        ...(cursor ? { cursor } : {}),
        // Redesigned Visits home: "Needs review (N)" counts each visit's
        // unconfirmed "[verify]" values.
        ...(AppConfig.enableAppRedesign
          ? { include: "needsReview" as const }
          : {}),
      },
    }),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    context: { expectedUserUid: value.uid },
    retry: false,
  });
}
export function visitDetailOptions(
  value: VisitScope,
  id: string,
): ReturnType<typeof orpc.getVisit.queryOptions<ApiOutputs["getVisit"]>> {
  return orpc.getVisit.queryOptions({
    queryKey: [...scopeKey(value), "detail", id],
    input: { params: { public_id: String(id) }, query: {} },
    context: { expectedUserUid: value.uid },
    retry: false,
  });
}
// Let a traversal finish before one coalesced post-write refresh.
const pendingRefresh = new Map<
  string,
  { promise: Promise<void>; phase: "waiting" | "refetching"; followUp: boolean }
>();
export function refreshVisitReads(
  value: VisitScope | null = getVisitScope(),
): Promise<void> {
  if (!value || getVisitScope() !== value) return Promise.resolve();
  const key = JSON.stringify(scopeKey(value));
  const pending = pendingRefresh.get(key);
  if (pending) {
    if (pending.phase === "refetching") pending.followUp = true;
    return pending.promise;
  }
  const entry: {
    promise: Promise<void>;
    phase: "waiting" | "refetching";
    followUp: boolean;
  } = { promise: Promise.resolve(), phase: "waiting", followUp: false };
  const refresh = (async (): Promise<void> => {
    const queryKey = scopeKey(value);
    const busy = (): boolean =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey })
        .some((query) => {
          if (!query.getObserversCount()) return false;
          if (query.state.fetchStatus !== "idle") return true;
          const data: unknown = query.state.data;
          if (
            query.state.status === "error" ||
            !data ||
            typeof data !== "object" ||
            !("pages" in data) ||
            !Array.isArray(data.pages)
          )
            return false;
          if (publicTraversalError(data.pages)) return false;
          const last: unknown = data.pages.at(-1);
          return (
            !!last &&
            typeof last === "object" &&
            "nextCursor" in last &&
            last.nextCursor !== null
          );
        });
    const waitForTraversal = (): Promise<void> =>
      new Promise((resolve) => {
        if (!busy()) {
          resolve();
          return;
        }
        const unsub = queryClient.getQueryCache().subscribe(() => {
          if (!busy()) {
            unsub();
            resolve();
          }
        });
        if (!busy()) {
          unsub();
          resolve();
        }
      });
    const run = (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    const flush = async (): Promise<void> => {
      entry.phase = "waiting";
      await waitForTraversal();
      if (getVisitScope() !== value) return;
      entry.phase = "refetching";
      await run();
      if (entry.followUp) {
        entry.followUp = false;
        await flush();
      }
    };
    await flush();
  })().finally(() => pendingRefresh.delete(key));
  entry.promise = refresh;
  pendingRefresh.set(key, entry);
  return refresh;
}
// Start visit on a pre-chart: the server moved the visit to Processing when
// it accepted the recording. Shown at once, before the refetch returns, so
// the draft (its Start visit button and editable note) never reappears and
// the merge progress shows for the whole wait.
export function markVisitProcessing(
  id: string,
  value: VisitScope | null = getVisitScope(),
): void {
  if (!value) return;
  queryClient.setQueriesData<{ status?: string | null }>(
    { queryKey: [...scopeKey(value), "detail", id] },
    (data) =>
      data && data.status === PRE_CHART_STATUS
        ? { ...data, status: StatusFile.PROCESSING }
        : data,
  );
}
interface VisitListRead {
  files: VisitSummary[];
  loading: boolean;
  error: boolean;
  ready: boolean;
  refresh: () => Promise<void>;
}
export function useVisitList(): VisitListRead {
  const current = useVisitScope();
  const query = useInfiniteQuery({
    ...visitListOptions(current ?? disabledScope),
    enabled: !!current,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (q) =>
      q.state.status === "error" ||
      publicTraversalError(q.state.data?.pages) ||
      q.state.fetchStatus !== "idle" ||
      q.state.data?.pages.at(-1)?.nextCursor
        ? false
        : REFRESH_AUDIO_INTERVAL,
  });
  const traversalError = publicTraversalError(query.data?.pages);
  const complete = useRef<
    | {
        scope: VisitScope;
        data: typeof query.data;
        files: VisitSummary[];
      }
    | undefined
  >(undefined);
  if (complete.current?.scope !== current) complete.current = undefined;
  if (
    current &&
    !traversalError &&
    query.isSuccess &&
    !query.isFetching &&
    !query.hasNextPage &&
    query.data &&
    complete.current?.data !== query.data
  ) {
    complete.current = {
      scope: current,
      data: query.data,
      files: sortVisitSummaries(query.data.pages.flatMap((page) => page.items)),
    };
  }
  const { hasNextPage, isFetching, isError, fetchNextPage } = query;
  useEffect(() => {
    if (current && hasNextPage && !isFetching && !isError && !traversalError)
      void fetchNextPage({ cancelRefetch: false });
  }, [
    current,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
    traversalError,
  ]);
  const refresh = useCallback(() => {
    if (!current || getVisitScope() !== current) return Promise.resolve();
    if (!query.isError && !traversalError) return refreshVisitReads(current);
    return queryClient.resetQueries({
      queryKey: [...scopeKey(current), "list"],
    });
  }, [current, query.isError, traversalError]);
  return {
    files: complete.current?.files ?? [],
    loading:
      !!current && !complete.current && !query.isError && !traversalError,
    error: query.isError || traversalError,
    ready: !!complete.current,
    refresh,
  };
}
export function isInaccessible(error: unknown): boolean {
  return (
    !!error &&
    typeof error === "object" &&
    (("code" in error &&
      ["NOT_FOUND", "UNAUTHORIZED", "FORBIDDEN"].includes(
        String(error.code),
      )) ||
      ("status" in error && [401, 403, 404].includes(Number(error.status))))
  );
}
function isTransientReadError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (!!error &&
      typeof error === "object" &&
      "status" in error &&
      typeof error.status === "number" &&
      error.status >= 500 &&
      error.status <= 599)
  );
}
// A visit (or recording) is still being worked on by the server while it is
// Processing or Generating; Ready, Error and anything else is settled.
const IN_PROGRESS = new Set<string>([
  StatusFile.PROCESSING,
  StatusFile.GENERATING,
]);
export const isInProgress = (status: string | null | undefined): boolean =>
  !!status && IN_PROGRESS.has(status);

export interface VisitDetailRead {
  file: VisitDetail | null;
  unavailable: boolean;
  loading: boolean;
  error: boolean;
  accepted: boolean;
  scope: VisitScope | null;
  refresh: () => Promise<void>;
}
export function useVisitDetail(id: string): VisitDetailRead {
  const current = useVisitScope();
  const validId = publicIdSchema.safeParse(id).success;
  const query = useQuery({
    ...visitDetailOptions(current ?? disabledScope, id),
    enabled: !!current && validId,
    retry: false,
    refetchInterval: (q) => {
      if (isInaccessible(q.state.error)) return false;
      if (isInProgress(q.state.data?.status)) return REFRESH_AUDIO_INTERVAL;
      return !q.state.data && isTransientReadError(q.state.error)
        ? 5000
        : false;
    },
  });
  const file = useMemo(
    () =>
      current && validId && query.data && !isInaccessible(query.error)
        ? toVisitDetail(query.data)
        : null,
    [current, validId, query.data, query.error],
  );
  const refresh = useCallback(() => refreshVisitReads(current), [current]);
  return {
    file,
    unavailable: !validId || isInaccessible(query.error),
    loading: !!current && validId && query.isPending,
    error: !validId || query.isError,
    accepted:
      !!current && validId && !!query.data && !isInaccessible(query.error),
    scope: current,
    refresh,
  };
}
